import {Component, ElementRef, Inject, LOCALE_ID, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Role} from '../../model/auth/Role';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ProgressService} from '../../service/progress.service';
import {BaseModalComponent} from '../../common/modal/base-modal/base-modal.component';
import {NotificationHandler} from '../../helper/notification.handler';
import {School, SchoolDto, SchoolType} from '../../model/location/School';
import {Region} from '../../model/location/Region';
import {LocationService} from '../../service/location.service';
import {CustomValidators} from '../../helper/custom.validators';
import {Observable} from 'rxjs';
import {Locale} from '../../model/Locale';
import {tap} from 'rxjs/operators';

export interface SchoolDetailsData {
  school?: School;
}

@Component({
  selector: 'app-school-modify-details-modal',
  templateUrl: './school-modify-details-modal.component.html',
  styleUrls: ['./school-modify-details-modal.component.scss']
})
export class SchoolModifyDetailsModalComponent extends BaseModalComponent implements OnInit {

  readonly roles = [Role.Admin, Role.Host];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;

  school?: School;
  region?: Region;
  type?: SchoolType;

  nameField = new FormControl('', [
    Validators.required, Validators.minLength(5), Validators.maxLength(256)
  ]);
  shortNameField = new FormControl('', [
    Validators.minLength(5), Validators.maxLength(128)
  ]);
  typeField = new FormControl('', [
    Validators.required
  ]);
  regionField = new FormControl('', [
    Validators.required
  ]);
  addressField = new FormControl('', [
    Validators.required, Validators.minLength(5), Validators.maxLength(128)
  ]);
  phoneField = new FormControl('', [
    CustomValidators.phoneNumberEx, Validators.minLength(7), Validators.maxLength(15)
  ]);
  emailField = new FormControl('', [
    Validators.email, Validators.minLength(5), Validators.maxLength(128)
  ]);
  websiteField = new FormControl('', [
    Validators.minLength(5), Validators.maxLength(128)
  ]);
  contactField = new FormControl('', [
    Validators.minLength(10), Validators.maxLength(128)
  ]);

  schoolForm: FormGroup = new FormGroup({
    name: this.nameField,
    shortName: this.shortNameField,
    type: this.typeField,
    region: this.regionField,
    address: this.addressField,
    phone: this.phoneField,
    email: this.emailField,
    website: this.websiteField,
    contact: this.contactField,
  });

  constructor(private locationService: LocationService,
              dialogRef: MatDialogRef<SchoolModifyDetailsModalComponent>,
              progressService: ProgressService,
              @Inject(MAT_DIALOG_DATA) data: SchoolDetailsData,
              @Inject(LOCALE_ID) private locale: string) {
    super(dialogRef, progressService);

    this.school = data?.school;
  }

  compareRegions(r1?: Region, r2?: Region): boolean {
    return r1 != undefined && r2 != undefined && r1.id === r2.id;
  }

  displayRegion(region: Region): string {
    return region.name;
  }

  loadRegions = (): Observable<Region[]> => {
    return this.locationService.getRegions(this.locale as Locale);
  };

  compareTypes(t1?: SchoolType, t2?: SchoolType): boolean {
    return t1 != undefined && t2 != undefined && t1.id === t2.id;
  }

  displayType(type: SchoolType): string {
    return type.name;
  }

  loadTypes = (): Observable<SchoolType[]> => {
    return this.locationService.getSchoolTypes(this.locale as Locale)
      .pipe(tap(types => {

        types.unshift(new SchoolType('-1', $localize`:General|All School Types term@@general-all-school-types-term:All types`));

        return types;
      }));
  };

  ngOnInit(): void {
    if (this.school) {
      this.nameField.setValue(this.school.name);
      this.shortNameField.setValue(this.school.short_name);
      this.type = new SchoolType(this.school.type, '');
      this.typeField.setValue(this.type);

      this.region = new Region(this.school.region_id as string, '');
      this.regionField.setValue(this.region);
      if(this.school.id) this.regionField.disable();

      this.addressField.setValue(this.school.address);
      this.phoneField.setValue(this.school.phone);
      this.emailField.setValue(this.school.email);
      this.websiteField.setValue(this.school.website);
      this.contactField.setValue(this.school.contact_name);
    }
  }

  proceedAllowed(): boolean {
    return this.schoolForm.valid;
  }

  handleRegionSelected(region: Region) {
    this.region = region;
    this.regionField.setValue(this.region);
  }

  typeSelected(type: SchoolType) {
    this.type = type;
    this.typeField.setValue(type);
  }

  proceed(): void {
    if(!this.region) return;

    this.startProgress();

    const data: SchoolDto = {
      id: this.school?.id || '',
      name: this.nameField.value as string,
      type: this.type?.id || '',
      region_id: this.region.id || '',
      region_name: this.region.name || '',
      address: this.addressField.value as string,
    };

    if(this.shortNameField.value) data.short_name = this.shortNameField.value as string;
    if(this.phoneField.value) data.phone = this.phoneField.value as string;
    if(this.emailField.value) data.email = this.emailField.value as string;
    if(this.websiteField.value) data.website = this.websiteField.value as string;
    if(this.contactField.value) data.contact_name = this.contactField.value as string;

    const observable = this.school?.id
      ? this.locationService.updateSchoolDetails(this.school.id, data)
      : this.locationService.createSchool(this.region.id, data);

    observable.subscribe(school => {
      const result: SchoolDetailsData = {
        school
      };
      this.closeModal(result);
    }, error => {
      if (this.school?.id) {
        NotificationHandler.handleError($localize`:Notifications|School details update failed message@@notifications-school-details-update-failed-error:School details update failed`, error);
      } else {
        NotificationHandler.handleError($localize`:Notifications|School creation failed message@@notifications-school-creation-failed-error:Adding new school failed`, error);
      }
      this.stopProgress();
    });
  }


}
